import { inject, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import pushNotificationService from '@/services/pushNotifications/pushNotification.service';
import { configurationKey } from '@/injections';
import useStickyAlerts from '@/modules/alerts/composables/useStickyAlerts';
import StickyAlert from '@/modules/alerts/models/sticky-alert';
import { STICKY_ALERT_TYPE } from '@/modules/alerts/models/sticky-alert-type';
import BasicStickyAlertProps, {
  BASIC_STICKY_ALERT_TYPE,
} from '@/modules/alerts/models/basic-sticky-alert-props';
import BrowserPermissionsError from '@/models/entities/push-notifications/browserPermissionsError';

export default function usePushNotifications() {
  const { t } = useI18n();
  const config = inject(configurationKey);
  const store = useStore();
  const { addAlert } = useStickyAlerts();
  const getUser = computed(() => store.getters['user/getUser']);
  const isEmployee = computed(() => store.getters['user/isEmployee']);

  const showErrorAlert = (errorMessage: string, subErrorMessage: string = null) => {
    const errorAlert = new StickyAlert(
      STICKY_ALERT_TYPE.BASIC,
      new BasicStickyAlertProps(BASIC_STICKY_ALERT_TYPE.ERROR, errorMessage, {
        timeoutMs: 15000,
        subMessage: subErrorMessage,
      }),
    );
    addAlert(errorAlert);
    errorAlert.getAnswer().then(a => console.debug('Alert dismissed: ', a));
  };

  const optOutOfNotifications = async (
    subErrorMessage: string = 'unableToOptInOutToPushNotifications',
  ) => {
    try {
      await pushNotificationService.attemptPushNotificationOptOut(
        getUser.value?.sub,
        config.webPushConfig,
        config.publicVapidKey,
      );
    } catch (error) {
      if (BrowserPermissionsError.isBrowserPermissionsError(error)) {
        console.error('Browser permissions error: ', error);
        showErrorAlert(
          t('errorTurningOffPushNotifications'),
          t('errorTurningOnPushNotificationsDueToBrowserWithoutSettings'),
        );
        throw error;
      }

      console.error('Error opting out of notifications: ', error);
      showErrorAlert(t('errorTurningOffPushNotifications'), t(subErrorMessage));
      throw error;
    }
  };

  const optInToNotifications = async (
    subBrowserErrorMessage: string = 'errorTurningOnPushNotificationsDueToBrowser',
    subGenericErrorMessage: string = 'unableToOptInOutToPushNotifications',
  ) => {
    try {
      await pushNotificationService.attemptPushNotificationOptIn(
        getUser.value?.sub,
        config.enabledPlatforms,
        config.webPushConfig,
        config.publicVapidKey,
        isEmployee.value,
      );
    } catch (error) {
      if (BrowserPermissionsError.isBrowserPermissionsError(error)) {
        console.error('Browser permissions error: ', error);
        showErrorAlert(t('errorTurningOnPushNotifications'), t(subBrowserErrorMessage));
        throw error;
      }

      await optOutOfNotifications();

      console.error('Error opting in to notifications: ', error);
      showErrorAlert(t('errorTurningOnPushNotifications'), t(subGenericErrorMessage));
      throw error;
    }
  };

  const isOptedInToNotifications = async (): Promise<boolean> => {
    try {
      return await pushNotificationService.isCustomerOptedInToPushNotifications(getUser.value?.sub);
    } catch (error) {
      console.log('Error checking opt-in status: ', error);
      return false;
    }
  };

  return {
    showErrorAlert,
    optInToNotifications,
    optOutOfNotifications,
    isOptedInToNotifications,
  };
}
